/**
 * Meridian UI entrypoint.
 *
 * Stream A PR 1 lands the real entrypoint shape: routing,
 * QueryClient provider, session-bootstrap context, chrome shell.
 * Subsequent PRs (Map, theme, multi-pane workspace) mount their
 * surfaces inside the chrome's canvas slot.
 *
 * Cross-references:
 *   - docs/attic/mvp/streams/A-ui-map.md.
 *   - docs/engineering/detail/storyboards/flows/01-login (Flow 01).
 *   - docs/engineering/detail/storyboards/shared/chrome-spec.md.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createBrowserRouter } from "react-router";
import { SessionProvider } from "@/auth/session-provider";
import { ErrorBoundary } from "@/chrome/error-boundary";
import { SseProvider } from "@/map/sse-provider";
import { SystemStatusStreamProvider } from "@/system-status/system-status-stream-provider";
import { MultiPaneApiProvider } from "@/multi-pane/multi-pane-context";
import { appRoutes } from "@/routes";
import "@/styles/globals.css";

// TanStack Query client. ``staleTime: 30s`` matches the BFF's
// session-throttle cadence so the UI doesn't re-fetch identical
// data more often than the BFF refreshes its underlying state.
// Retry twice on transient failures; never retry on 401 / 403
// (the API client maps those to login redirect / forbidden page).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        if (error instanceof Response && (error.status === 401 || error.status === 403)) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
  },
});

// The route table lives in ``routes.tsx`` so a test can walk it — every
// route carries an ``errorElement`` (REQ-UI-191). Stream C PR 1 mounted the
// multi-pane workspace at the index route per ADR-024 §2.
const router = createBrowserRouter(appRoutes);

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("Meridian UI: root element missing from index.html");
}

createRoot(rootEl).render(
  <StrictMode>
    {/* Root render-error boundary (REQ-UI-191). A crash in any provider
     * below would otherwise unmount the whole tree and leave a blank
     * document — React's default for an uncaught render error. Panels and
     * routes have their own, finer boundaries (panel-registry.tsx,
     * routes.tsx); this one is the last resort and offers only a reload. */}
    <ErrorBoundary scope="page" surface="Meridian" testId="app-error-fallback">
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <SseProvider>
            {/* SystemStatusStreamProvider — REQ-STATUS-048. The system-status
             * SSE is held at application scope, beside the map stream and for
             * the same reason: the top bar's system rollup badge is chrome and
             * must keep updating while the System panel is closed, undocked, or
             * has never been opened. Panel-owned, it froze at its first value
             * (the snapshot hook has no refetchInterval to cover for it), which
             * is a permanently green pill over a dead feed — REQ-STATUS-016's
             * false green. Inside SessionProvider so the role gate can read the
             * session and so an ended session unmounts (and closes) the stream
             * per REQ-STATUS-062. One EventSource per tab (REQ-UI-035). */}
            <SystemStatusStreamProvider>
              {/* The selection bus is now a module-level Zustand store
               * (``@/selection/selection-store``) and needs no
               * provider — consumers subscribe directly via narrow
               * selector hooks. Stream A PR 5.1 hoisted from the M5
               * ``<SelectionProvider>`` per REQ-UI-027 + ADR-024 §5.
               *
               * MultiPaneApiProvider — Stream C PR 1. Bridges the
               * topbar's :class:`PreferencesMenu` (renders inside
               * AppShell's chrome) to the route-mounted
               * :class:`MultiPaneWorkspace` (renders inside the
               * router's ``<Outlet />``). The menu reads the
               * workspace's bound action surface via
               * :func:`useMultiPaneApi`; returns ``null`` when off
               * the ``/`` route so the Layout section hides.
               * Cross-references: ADR-024 §3 / 6; REQ-UI-048 / 052 / 055.
               */}
              <MultiPaneApiProvider>
                {/* The classification marking moved from a top/bottom
                 * ClassificationFrame banner to a top-bar badge (REQ-UI-058,
                 * 2026-06-12 — see chrome/classification-badge.tsx, mounted in
                 * the top bar by AppShell). The router mounts directly and
                 * AppShell owns the viewport height (h-screen, formerly the
                 * frame's job). The authenticated GET /api/config fetch
                 * (REQ-UI-060) that drives the badge runs inside the session,
                 * with the cookie present. */}
                <RouterProvider router={router} />
              </MultiPaneApiProvider>
            </SystemStatusStreamProvider>
          </SseProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
